import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { observer } from 'mobx-react-lite';
import {
  AsyncState,
  BookingSummary,
  PageLoading,
  StationPicker,
  StationSelector,
  TimeRangeTabs,
  TrainCard,
  TrainResults,
  type AvailabilityTone
} from '@link/share/rail-booking';

import { useStore } from '@link/emot/stores';
import {
  RemainStatus,
  type Diagram,
  type Station,
  type TimeRange
} from '@link/emot/services/types';
import { getSafeReturnUrl, saveOrderFlowContext } from '@link/emot/utils';

import styles from './trainList.module.css';

interface StationPickerState {
  stations: StationPickerOption[];
  selectedCode?: string;
  excludeCode?: string;
  onSelect: (station: StationPickerOption) => void;
}

interface StationPickerOption {
  code: string;
  name: string;
  source: Station;
}

const STATUS_META: Record<
  RemainStatus,
  { symbol: string; tone: AvailabilityTone; key: string; actionable: boolean }
> = {
  [RemainStatus.Available]: {
    symbol: '○',
    tone: 'available',
    key: 'views.trainList.seatAvailable',
    actionable: true
  },
  [RemainStatus.Limited]: {
    symbol: '△',
    tone: 'limited',
    key: 'views.trainList.seatLimited',
    actionable: true
  },
  [RemainStatus.SoldOut]: {
    symbol: '✕',
    tone: 'unavailable',
    key: 'views.trainList.soldOut',
    actionable: false
  },
  [RemainStatus.Unknown]: {
    symbol: '—',
    tone: 'unknown',
    key: 'views.trainList.notOnSale',
    actionable: false
  }
};

const joinStations = (stations: Station[]) =>
  stations.map(station => station.name).join('/');

const toStationOption = (station?: Station): StationPickerOption | undefined =>
  station
    ? {
        code: station.code ?? '',
        name: station.name ?? '',
        source: station
      }
    : undefined;

const toStationOptions = (stations: Station[]) =>
  stations
    .map(toStationOption)
    .filter((station): station is StationPickerOption => Boolean(station));

const TrainListView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token, bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const tUrl = searchParams.get('t_url');
  const { emot } = useStore();
  const isChange = typeParam === 'change';
  const {
    bookingInfo,
    depStations,
    arrStations,
    diagrams,
    orderableTimeRanges,
    selection
  } = emot;
  const [stationPicker, setStationPicker] = useState<StationPickerState | null>(
    null
  );
  const [timeRange, setTimeRange] = useState<TimeRange | null>(null);
  const [loadingStations, setLoadingStations] = useState(false);
  const [stationsError, setStationsError] = useState(false);
  const [stationsRetryKey, setStationsRetryKey] = useState(0);
  const [loadingTrains, setLoadingTrains] = useState(true);
  const [trainSearchError, setTrainSearchError] = useState(false);
  const [trainSearchRetryKey, setTrainSearchRetryKey] = useState(0);
  const [bookingError, setBookingError] = useState(false);
  const bookingRequestId = useRef(0);
  const stationsRequestId = useRef(0);
  const trainSearchRequestId = useRef(0);

  useEffect(() => {
    if (token) {
      emot.setToken(token);
    }
    if (bookingId) {
      emot.setBookingId(bookingId);
    }
    const orderType = typeParam === 'change' ? 'change' : 'booking';
    const returnUrl = tUrl ? (getSafeReturnUrl(tUrl) ?? '') : '';
    emot.setOrderType(orderType);
    emot.setTUrl(returnUrl);
    if (token && bookingId) {
      saveOrderFlowContext(token, bookingId, {
        orderType,
        tUrl: returnUrl
      });
    }
  }, [bookingId, emot, tUrl, token, typeParam]);

  useEffect(() => {
    const requestId = ++bookingRequestId.current;
    const targetBookingId = bookingId || emot.bookingId || undefined;
    if (
      !bookingInfo ||
      (targetBookingId && bookingInfo.bookingId !== targetBookingId)
    ) {
      setBookingError(false);
      void emot.getBookingData(targetBookingId).catch(() => {
        if (requestId === bookingRequestId.current) {
          setBookingError(true);
        }
      });
    }
  }, [bookingId, bookingInfo, emot]);

  useEffect(() => {
    const requestId = ++stationsRequestId.current;
    if (!bookingInfo) return;
    setStationsError(false);
    setLoadingStations(true);
    void emot
      .getStations()
      .catch(() => {
        if (requestId === stationsRequestId.current) {
          setStationsError(true);
        }
      })
      .finally(() => {
        if (requestId === stationsRequestId.current) {
          setLoadingStations(false);
        }
      });
  }, [bookingInfo, emot, stationsRetryKey]);

  useEffect(() => {
    setTimeRange(current =>
      current &&
      orderableTimeRanges.some(
        range => range.from === current.from && range.to === current.to
      )
        ? current
        : null
    );
  }, [orderableTimeRanges]);

  const fromCode = selection.fromStation?.code;
  const toCode = selection.toStation?.code;
  useEffect(() => {
    const requestId = ++trainSearchRequestId.current;
    if (!timeRange || !fromCode || !toCode || !bookingInfo?.departDate) {
      setLoadingTrains(false);
      return;
    }
    setTrainSearchError(false);
    setLoadingTrains(true);
    void emot
      .searchDiagrams(timeRange)
      .catch(() => {
        if (requestId === trainSearchRequestId.current) {
          emot.setDiagrams([]);
          setTrainSearchError(true);
        }
      })
      .finally(() => {
        if (requestId === trainSearchRequestId.current) {
          setLoadingTrains(false);
        }
      });
  }, [
    bookingInfo?.departDate,
    emot,
    fromCode,
    timeRange,
    toCode,
    trainSearchRetryKey
  ]);

  if (!bookingInfo) {
    if (!bookingId && !emot.bookingId) {
      return (
        <AsyncState
          status="empty"
          title={t('common.noBooking')}
          message={t('common.openFromBooking')}
        />
      );
    }
    if (bookingError) {
      return (
        <AsyncState
          status="error"
          title={t('common.loadFailed')}
          actionLabel={t('common.retry')}
          onAction={() => location.reload()}
        />
      );
    }
    return <PageLoading />;
  }

  if (stationsError) {
    return (
      <AsyncState
        status="error"
        title={t('common.loadFailed')}
        actionLabel={t('common.retry')}
        onAction={() => setStationsRetryKey(key => key + 1)}
      />
    );
  }

  if (loadingStations && orderableTimeRanges.length === 0) {
    return <PageLoading />;
  }

  const { fromStation, toStation, routeReversed } = selection;
  const departureOptions = routeReversed ? arrStations : depStations;
  const arrivalOptions = routeReversed ? depStations : arrStations;
  const quantities =
    bookingInfo.units?.map(
      unit => `${unit.name ?? ''} ×${unit.quantity ?? 0}`
    ) ?? [];
  const preOrderNote = isChange
    ? t('views.change.policy')
    : t('views.trainList.preOrderNote');

  const handleSelectTrain = (diagram: Diagram) => {
    emot.setDiagram(diagram);
    navigate('seat');
  };

  const handleSwapStations = () => {
    if (loadingTrains || isChange) return;
    setLoadingTrains(true);
    emot.swapStations();
  };

  const openDeparturePicker = () => {
    setStationPicker({
      stations: toStationOptions(departureOptions),
      selectedCode: fromStation?.code,
      excludeCode: toStation?.code,
      onSelect: station => {
        if (toStation) emot.setStations(station.source, toStation);
        setStationPicker(null);
      }
    });
  };

  const openArrivalPicker = () => {
    setStationPicker({
      stations: toStationOptions(arrivalOptions),
      selectedCode: toStation?.code,
      excludeCode: fromStation?.code,
      onSelect: station => {
        if (fromStation) emot.setStations(fromStation, station.source);
        setStationPicker(null);
      }
    });
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.sectionTitle}>
        {t('views.trainList.vehicleInfo')}
      </h1>
      <BookingSummary
        data={{
          ticketName: t('views.trainList.ticketName'),
          fromStation: joinStations(depStations),
          toStation: joinStations(arrStations),
          travelDate: bookingInfo.departDate,
          quantities,
          seatClass: bookingInfo.seatClassName
        }}
        labels={{
          route: t('views.trainList.route'),
          travelDate: t('views.trainList.travelDate'),
          quantities: t('views.trainList.ticketCount'),
          trainType: t('views.trainList.trainType'),
          seatClass: t('views.trainList.seatClass'),
          seatPreference: t('views.trainList.preference')
        }}
      />
      {preOrderNote && (
        <p className={isChange ? styles.changePolicy : styles.note}>
          {preOrderNote}
        </p>
      )}

      <h2 className={styles.sectionTitle}>
        {t('views.trainList.selectStation')}
      </h2>
      <StationSelector
        fromLabel={t('views.trainList.from')}
        toLabel={t('views.trainList.to')}
        swapLabel={t('views.trainList.swap')}
        fromStation={toStationOption(fromStation)}
        toStation={toStationOption(toStation)}
        disabled={loadingTrains || isChange}
        onSelectFrom={openDeparturePicker}
        onSelectTo={openArrivalPicker}
        onSwap={handleSwapStations}
      />

      <h2 className={styles.sectionTitle}>
        {t('views.trainList.selectTrain')}
      </h2>
      <TimeRangeTabs
        ranges={orderableTimeRanges}
        value={timeRange}
        disabled={loadingTrains}
        onChange={setTimeRange}
      />

      {orderableTimeRanges.length === 0 && (
        <AsyncState
          status="empty"
          message={t('views.trainList.noTimeRanges')}
        />
      )}

      <TrainResults
        active={Boolean(timeRange)}
        loading={loadingTrains}
        error={trainSearchError}
        empty={diagrams.length === 0}
        loadFailedTitle={t('common.loadFailed')}
        retryLabel={t('common.retry')}
        emptyMessage={t('views.trainList.noTrains')}
        onRetry={() => setTrainSearchRetryKey(key => key + 1)}
      >
        {diagrams.map(diagram => {
          const meta =
            STATUS_META[diagram.remainStatus ?? RemainStatus.Unknown];
          return (
            <TrainCard
              key={diagram.diagramCode}
              train={{
                id: diagram.diagramCode ?? '',
                departTime: diagram.departTime,
                arriveTime: diagram.arriveTime,
                duration: diagram.timeDuration,
                trainName: [
                  diagram.trainName,
                  diagram.trainMode ? `(${diagram.trainMode})` : undefined
                ]
                  .filter(Boolean)
                  .join(' '),
                departureStation: fromStation?.name,
                arrivalStation: toStation?.name,
                availabilityLabel: t(meta.key),
                availabilitySymbol: meta.symbol,
                availabilityTone: meta.tone,
                actionable: meta.actionable
              }}
              selectLabel={t('views.trainList.select')}
              unavailableLabel={t('views.trainList.book')}
              onSelect={() => handleSelectTrain(diagram)}
            />
          );
        })}
      </TrainResults>

      <StationPicker
        visible={stationPicker !== null}
        title={t('views.trainList.selectStation')}
        closeLabel={t('common.cancel')}
        stations={stationPicker?.stations ?? []}
        selectedCode={stationPicker?.selectedCode}
        excludeCode={stationPicker?.excludeCode}
        onSelect={station => stationPicker?.onSelect(station)}
        onClose={() => setStationPicker(null)}
      />
    </div>
  );
};

const TrainList = observer(TrainListView);

export default TrainList;
